import React, { useState } from 'react'
import {
  type CreateDisbursementInstructionForm,
  type CreateDisbursementInstructionPayload, validateCreateDisbursementInstructionPayload
} from '../../../../api/schema-dtos/AssetHolderDisbursementPayloadDtos'
import { useCreateDisbursementInstructionMutation } from '../../../../api/disbursements/disbursementApi'
import { setNotification } from '../../../shared/components/ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../../api/shared/RtkQueryHelper'
import { useAppDispatch } from '../../../../app/hooks'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid, InputLabel, MenuItem, Select,
  TextField,
  Typography
} from '@mui/material'
import ErrorComponent from '../../../shared/components/ui-widgets/ErrorComponent'
import { Formik, type FormikProps } from 'formik'
import OtpComponent from '../../../auth/components/OtpComponent'
import {
  type DisbursementAccount,
  type DisbursementFeeSummary
} from '../../../../api/schema-dtos/AssetHolderDisbursementDomainDtos'
import { DisbursementSchedule } from '../../../../api/disbursements/DisbursementSchedule'
import { Currency } from '../../../../api/asset-holders/Currency'
import { type LedgerAccountDetails } from '../../../../api/schema-dtos/AssetHolderFinancePayloadDtos'

interface CreateDisbursementInstructionComponentProps {
  assetHolderId: number
  hasPassedKyc: boolean
  disbursementFees: DisbursementFeeSummary[]
  disbursementAccounts: DisbursementAccount[]
  wallets: LedgerAccountDetails[]
  maxCardWidth: number
}

const CreateDisbursementInstructionComponent: React.FC<CreateDisbursementInstructionComponentProps> = ({
  assetHolderId,
  disbursementFees,
  hasPassedKyc,
  disbursementAccounts,
  wallets,
  maxCardWidth
}: CreateDisbursementInstructionComponentProps) => {
  const dispatch = useAppDispatch()
  const [openDialog, toggleDialog] = useState<boolean>(false)

  const filteredWallets: LedgerAccountDetails[] = wallets.filter(w => w.availableBalance > 0)
  let filteredDisbursementAccounts: DisbursementAccount[] = []

  const defaultForm: CreateDisbursementInstructionForm = {
    currencyCode: '',
    disbursementAccountId: null,
    payoutSchedule: DisbursementSchedule.WEEKLY,
    requestedPayoutAmount: 0
  }

  const [apiCreateDisbursementInstruction, { isLoading }] = useCreateDisbursementInstructionMutation()

  const handleCreateDisbursementInstruction = (formData: CreateDisbursementInstructionForm, otp: string): void => {
    const payload: CreateDisbursementInstructionPayload = {
      disbursementAccountId: formData.disbursementAccountId ?? 0,
      assetHolderId,
      otp,
      currencyCode: formData.currencyCode,
      requestedPayoutAmount: formData.requestedPayoutAmount,
      payoutSchedule: formData.payoutSchedule
    }

    apiCreateDisbursementInstruction({ assetHolderId, body: payload })
      .unwrap()
      .then(() => {
        dispatch(setNotification({
          open: true,
          severity: 'success',
          message: 'Your cash out instruction has been successfully scheduled. You will also receive a confirmation email.'
        }))
        toggleDialog(false)
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  const updateSelectedCurrency = async (formikProps: FormikProps<CreateDisbursementInstructionForm>, currencyCode: Currency): Promise<void> => {
    await formikProps.setFieldValue('currencyCode', currencyCode)
    const currencyWallet: LedgerAccountDetails | undefined = wallets.find(w => w.currencyCode === currencyCode)
    filteredDisbursementAccounts = disbursementAccounts.filter(da => da.currencyCode === currencyCode && da.verifiedAndActive)

    if (currencyWallet !== undefined) {
      await formikProps.setFieldValue('requestedPayoutAmount', currencyWallet.availableBalance)
    }
  }

  const getDisbursementAccountName = (disbursementAccount: DisbursementAccount): string => {
    let accountName: string = ''
    if (disbursementAccount.accountDetails.accountType === 'BitcoinAddress') {
      accountName = `Bitcoin: ${disbursementAccount.accountDetails.bitcoinAddress}`
    }

    if (disbursementAccount.accountDetails.accountType === 'BankAccount') {
      accountName = `Bank: ${disbursementAccount.accountDetails.bankName} ${disbursementAccount.accountDetails.bankAccountNumber}`
    }

    return accountName
  }

  const getFee = (currencyCode: string, payoutSchedule: string): number => {
    const fee = disbursementFees.find(x => x.currencyCode === currencyCode && x.payoutSchedule === payoutSchedule)
    if (fee === undefined) return 0

    return fee.amount
  }

  const roundForCurrency = (amount: number, decimal: number): string => {
    return amount.toFixed(decimal)
  }

  const getAmountToPay = (fee: number, requestedAmount: number, currencyCode: string): string => {
    return roundForCurrency((requestedAmount - fee), (currencyCode === 'ZAR' ? 2 : 8))
  }

  const displayCurrencyCode = (values: CreateDisbursementInstructionForm): string => {
    if (values.currencyCode === Currency.XBT) {
      return 'BTC'
    }
    return values.currencyCode
  }

  const formatError = (formikProps: FormikProps<CreateDisbursementInstructionForm>): string => {
    return `You cannot create your cash out instruction until the following issues have been resolved: ${Object.values(formikProps.errors).join(', ')}`
  }

  return (
    <>

      {filteredWallets.length === 0 && (
        <Box>
          <Alert severity="info" sx={{ mt: 3, maxWidth: maxCardWidth - 30, px: 2 }}>
            Cash out instructions can not be created as all wallets have a zero balance.
          </Alert>
        </Box>
      )}

      {filteredWallets.length > 0 && (

          <Button
          variant={'contained'}
          color={'primary'}
          sx={{ mt: 2, mr: 2 }}
          disabled={!hasPassedKyc}
          onClick={() => {
            toggleDialog(true)
          }}
        >
          Schedule a cash out
        </Button>

      )}
      <Dialog
        open={openDialog}
        maxWidth={'md'}
        fullWidth={true}
        disableEscapeKeyDown={true}
        onClose={() => {
          toggleDialog(false)
        }}
        sx={{ p: 2 }}
      >
        <DialogTitle fontWeight={'bold'}>Schedule a cash out</DialogTitle>

        <DialogContent>
          <Formik
            initialValues={defaultForm}
            validationSchema={validateCreateDisbursementInstructionPayload}
            validateOnBlur={true}
            validateOnChange={true}
            validateOnMount={true}
            onSubmit={(values) => {
            }}
          >
            {formikProps => (
              <form>
                <Grid container spacing={3} sx={{ pt: 2 }}>

                  <Grid item md={12} xs={12} sx={{ mt: 1 }}>
                    <Typography variant={'h4'} sx={{ mb: 1 }}>
                      Terms of Settlement and Withdrawal
                    </Typography>
                    {/* <Typography variant={'body1'}>{settlementDto.termsAndIndemnityNotice}</Typography> */}
                    <Typography variant={'body1'}>
                      The amount(s) payable into your wallet as communicated to you constitute the full amount payable by
                      The Sun Exchange (SA) Bewind Trust and all entities within the Sun Exchange group of companies
                      (&quot;The Sun Exchange&quot;) in respect of your cell ownership for the projects in this settlement, and you will
                      have no further claims of any nature whatsoever against The Sun Exchange, whether pursuant to your cell
                      ownership or otherwise. You will be entitled to withdraw the funds from your wallet, and The Sun  Exchange
                      will facilitate such withdrawal, upon provision by you of the requisite banking and other detail and
                      information required by The Sun Exchange to facilitate the withdrawal.  By exercising the withdrawal
                      of the funds in your wallet, you assume all risk and liability of any nature whatsoever in respect of the
                      aforementioned withdrawal, and hereby fully indemnify and hold The Sun Exchange  harmless against all or
                      any liability, loss, damages, claims, penalties, costs (including legal costs) or fines in relation to or
                      arising from the  withdrawal. This indemnity is in addition to, and does not replace, any other indemnities
                      as provided for in the Terms of Service, or any other agreement with The Sun Exchange.
                    </Typography>

                  </Grid>

                  {!(formikProps.isValid) && (
                    <Grid item md={12} sx={{ mt: 3 }}>
                      <ErrorComponent title={'Form validation error'} message={formatError(formikProps)}/>
                    </Grid>
                  )}

                  <Grid item md={12} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Cash out currency</InputLabel>
                      <Select
                        {...formikProps.getFieldProps('currencyCode')}
                        label="Cash out currency"
                        onChange={async event => {
                          await updateSelectedCurrency(formikProps, event.target.value as Currency)
                        }}
                      >
                        {filteredWallets.map((wallet: LedgerAccountDetails, index: number) => (
                          <MenuItem key={index} value={wallet.currencyCode}>{wallet.currencyDisplayCode}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item md={12} xs={12}>
                    <TextField
                      label={'Cash out amount'}
                      type={'number'}
                      variant="outlined"
                      sx={{ width: '100%' }}
                      disabled={true}
                      placeholder={'Cash out amount'}

                      {...formikProps.getFieldProps('requestedPayoutAmount')}

                    />
                  </Grid>

                  <Grid item md={12} xs={12}>
                    {/* <Autocomplete */}
                    {/*  options={filteredDisbursementAccounts} */}
                    {/*  disabled={filteredDisbursementAccounts.length === 0} */}
                    {/*  {...formikProps.getFieldProps('disbursementAccountId')} */}
                    {/*  onChange={async (event: any, value: any) => await formikProps.setFieldValue('disbursementAccountId', value)} */}
                    {/*  getOptionLabel={(option: DisbursementAccount) => getDisbursementAccountName(option)} */}
                    {/*  renderInput={(params) => ( */}
                    {/*    <TextField */}
                    {/*      {...params} */}
                    {/*      variant="outlined" */}
                    {/*      required={true} */}
                    {/*      label="Cash out account" */}
                    {/*      placeholder="Please select a cash out account" */}
                    {/*      error={formikProps.errors.disbursementAccountId !== undefined} */}
                    {/*      helperText={formikProps.errors.disbursementAccountId} */}
                    {/*    /> */}
                    {/*  )} */}
                    {/* /> */}

                    <FormControl fullWidth>
                      <InputLabel>Account Details</InputLabel>

                      <Select
                        {...formikProps.getFieldProps('disbursementAccountId')}
                        label="Cash Out account"
                        onChange={async event => await formikProps.setFieldValue('disbursementAccountId', event.target.value)}
                      >
                        {filteredDisbursementAccounts.map((da) => (
                          <MenuItem key={da.disbursementAccountId}
                                    value={da.disbursementAccountId}>{getDisbursementAccountName(da)}</MenuItem>
                        ))}

                      </Select>
                    </FormControl>
                  </Grid>
                  {/* <Grid item md={12} xs={12}> */}
                  {/*  <Typography variant={'subtitle1'}>Account Details</Typography> */}
                  {/* </Grid> */}

                  <Grid item md={12} xs={12}>
                    <Typography variant={'body2'}>Your cash out request will be processed on the next Wednesday. </Typography>
                  </Grid>

                  {(formikProps.values.currencyCode !== '') && (formikProps.values.payoutSchedule !== '') &&
                    <Grid item md={12}>
                      <Grid container spacing={2}>
                        <Grid item md={12} xs={12}>
                          {formikProps.values.payoutSchedule === DisbursementSchedule.IMMEDIATE && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                              Transaction
                              fee: {displayCurrencyCode(formikProps.values)} {getFee(formikProps.values.currencyCode, formikProps.values.payoutSchedule)}
                            </Alert>
                          )}
                          {formikProps.values.payoutSchedule === DisbursementSchedule.WEEKLY && (
                            <Typography variant={'body1'}>Transaction
                              fee: {displayCurrencyCode(formikProps.values)} {getFee(formikProps.values.currencyCode, formikProps.values.payoutSchedule)}</Typography>
                          )}
                        </Grid>
                        <Grid item md={12} xs={12}>
                          <Typography variant={'body1'}>You have requested to cash
                            out: {displayCurrencyCode(formikProps.values)} {formikProps.values.requestedPayoutAmount}</Typography>
                        </Grid>
                        <Grid item md={12} xs={12}>
                          <Typography variant={'body1'}>Amount to be paid out to your
                            account: {displayCurrencyCode(formikProps.values)} {getAmountToPay(getFee(formikProps.values.currencyCode, formikProps.values.payoutSchedule), formikProps.values.requestedPayoutAmount, formikProps.values.currencyCode)}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  }

                  <Grid item md={12}>
                    <Box display={'flex'} justifyContent={'space-between'}>
                      <OtpComponent
                        assetHolderId={assetHolderId}
                        actionName={'Confirm'}
                        isLoading={isLoading}
                        authenticatorConfigured={null}
                        executeAction={(otp: string) => {
                          handleCreateDisbursementInstruction(formikProps.values, otp)
                        }}
                        disabled={!formikProps.isValid}
                      />

                      <Button
                        variant={'outlined'}
                        type="button"
                        sx={{ ml: 2 }}
                        onClick={() => {
                          toggleDialog(false)
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

    </>
  )
}

export default CreateDisbursementInstructionComponent
